import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Button,
  Dialog,
  CircularProgress,
  Alert,
  Snackbar,
  Paper,
  Fade,
  useTheme,
  Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import GroupsIcon from '@mui/icons-material/Groups';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useAuth } from '../context/AuthContext';
import AddGroupForm from '../components/AddGroupForm';
import EditGroupForm from '../components/EditGroupForm';
import InviteMembers from '../components/InviteMembers';
import axios from '../utils/axiosConfig';
import GroupManagement from '../components/GroupManagement';
import { toast } from 'react-hot-toast';

const GroupPage = () => {
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { token, user, isAuthenticated } = useAuth();
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [authReady, setAuthReady] = useState(false);
  const [group, setGroup] = useState(null);
  const { user: currentUser } = useAuth();

  const theme = useTheme();

  useEffect(() => {
    console.log('Auth state changed:', {
      isAuthenticated,
      user,
      'user?.team_id': user?.team_id
    });
  }, [isAuthenticated, user]);

  useEffect(() => {
    const initializePage = async () => {
      if (!isAuthenticated || !user) {
        setLoading(true);
        return;
      }

      await new Promise(resolve => setTimeout(resolve, 100));

      try {
        if (!user.team_id && (user.role === 'corporate' || user.role === 'admin')) {
          setError('No team association found. Please contact your administrator.');
          setLoading(false);
          return;
        }

        setLoading(true);
        await Promise.all([
          fetchGroups(),
          fetchAvailableUsers()
        ]);
        
      } catch (error) {
        console.error('Error initializing page:', error);
        setError('Failed to load page data');
      } finally {
        setLoading(false);
      }
    };

    initializePage();
  }, [user, isAuthenticated]);

  const fetchGroups = async () => {
    if (!user?.team_id) {
      console.log('Skipping group fetch - no team ID:', user);
      return;
    }

    try {
      console.log('Fetching groups for team:', user.team_id);
      const endpoint = `/groups/team/${user.team_id}`;
      const response = await axios.get(endpoint);
      
      if (response.data.success) {
        const groups = response.data.groups.map(group => ({
          ...group,
          members: group.members || []
        }));
        console.log('Groups fetched successfully:', groups);
        setGroups(groups);
        setError(null);
        
        // Also refresh available users when groups are updated
        await fetchAvailableUsers();
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
      setError('Failed to fetch groups');
    }
  };

  const fetchAvailableUsers = async () => {
    try {
        console.log('Fetching available users...');
        const response = await axios.get('/api/team/members');
        console.log('Team members response:', response.data);
        
        if (response.data.success) {
            const filteredUsers = response.data.members.filter(
                member => member.id !== user.id
            );
            setAvailableUsers(filteredUsers);
        } else {
            console.error('Failed to fetch team members:', response.data.message);
        }
    } catch (error) {
        console.error('Error fetching team members:', error.response?.data || error.message);
        setError('Failed to fetch team members');
    }
  };

  const handleAddGroup = async (groupData) => {
    if (!user?.team_id) {
      showSnackbar('Team association required to create groups', 'error');
      return;
    }

    try {
      const response = await axios.post('/groups', {
        ...groupData,
        team_id: user.team_id
      });

      if (response.data.success) {
        setGroups(prevGroups => [...prevGroups, response.data.group]);
        setAddDialogOpen(false);
        showSnackbar('Group created successfully');
        await fetchGroups();
      }
    } catch (error) {
      console.error('Error creating group:', error);
      showSnackbar(error.response?.data?.message || 'Failed to create group', 'error');
    }
  };

  const handleEditGroup = async (groupId, updatedData) => {
    try {
      console.log('Editing group:', { groupId, updatedData });
      const response = await axios.put(`/groups/${groupId}`, updatedData);
      
      if (response.data.success) {
        showSnackbar('Group updated successfully', 'success');
        await fetchGroups();
        setEditDialogOpen(false); // Close the dialog after successful edit
        setEditingGroup(null); // Reset the editing group
      } else {
        throw new Error(response.data.message || 'Failed to update group');
      }
    } catch (error) {
      console.error('Error updating group:', error);
      showSnackbar(
        error.response?.data?.message || error.message || 'Failed to update group',
        'error'
      );
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (window.confirm('Are you sure you want to delete this group?')) {
      try {
        const response = await axios.delete(`/groups/${groupId}`);
        
        if (response.data.success) {
          showSnackbar('Group deleted successfully', 'success');
          await fetchGroups();
        } else {
          throw new Error(response.data.message || 'Failed to delete group');
        }
      } catch (error) {
        console.error('Error deleting group:', error);
        showSnackbar(
          error.response?.data?.message || error.message || 'Failed to delete group',
          'error'
        );
      }
    }
  };

  const handleAddMember = async (groupId, selectedUsers) => {
    try {
        console.log('Adding members:', { groupId, selectedUsers });
        
        const userIds = Array.isArray(selectedUsers) ? 
            selectedUsers.map(user => user.id) : 
            [selectedUsers].map(user => user.id);

        console.log('User IDs to add:', userIds);

        const response = await axios.post(`/groups/${groupId}/members`, {
            userIds
        });

        if (response.data.success) {
            await fetchGroups(); // Wait for the groups to refresh
            toast.success('Members added successfully');
            setSelectedUsers([]);
        }
    } catch (error) {
        console.error('Error adding members:', error);
        toast.error(error.response?.data?.message || 'Failed to add members to group');
    }
  };

  const handleRemoveMember = async (groupId, memberId) => {
    try {
        console.log('Removing member:', { groupId, memberId });
        
        const response = await axios.delete(`/groups/${groupId}/members/${memberId}`);

        if (response.data.success) {
            await fetchGroups(); // Wait for the groups to refresh
            toast.success('Member removed successfully');
        } else {
            throw new Error(response.data.message || 'Failed to remove member');
        }
    } catch (error) {
        console.error('Error removing member:', error);
        toast.error(error.response?.data?.message || 'Failed to remove member from group');
    }
  };

  const handleInviteMember = (group) => {
    setSelectedGroup(group);
    setInviteDialogOpen(true);
  };

  const handleOpenInviteDialog = () => {
    setSelectedGroup(null);
    setInviteDialogOpen(true);
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const isAdmin = group?.members?.some(member => 
    member.id === currentUser?.id && 
    member.group_members?.role === 'admin'
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header Section */}
      <Paper 
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          background: theme.palette.background.default,
          borderRadius: 2
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <GroupsIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
            <Box>
              <Typography variant="h4" fontWeight="bold" color="primary">
                Groups
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Manage your team groups and members
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3
            }}
          >
            Create Group
          </Button>
        </Box>
      </Paper>

      {loading ? (
        <Box 
          display="flex" 
          flexDirection="column" 
          alignItems="center" 
          justifyContent="center" 
          minHeight="300px"
        >
          <CircularProgress sx={{ mb: 2 }} />
          <Typography color="text.secondary">
            Loading groups...
          </Typography>
        </Box>
      ) : error ? (
        <Fade in>
          <Alert 
            severity="error" 
            sx={{ 
              borderRadius: 2,
              '& .MuiAlert-message': { fontSize: '1rem' }
            }}
          >
            {error}
          </Alert>
        </Fade>
      ) : groups.length === 0 ? (
        <Fade in>
          <Paper
            sx={{
              p: 4,
              borderRadius: 2,
              textAlign: 'center',
              background: theme.palette.background.paper
            }}
          >
            <GroupsIcon 
              sx={{ 
                fontSize: 60, 
                color: theme.palette.text.secondary,
                mb: 2
              }} 
            />
            <Typography variant="h6" gutterBottom>
              No Groups Found
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ mb: 3 }}
            >
              Get started by creating your first group or refresh the page if you believe this is an error.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddDialogOpen(true)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: 3,
                mr: 2
              }}
            >
              Create Group
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchGroups}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: 3
              }}
            >
              Refresh
            </Button>
          </Paper>
        </Fade>
      ) : (
        <Fade in>
          <Box>
            {/* Replace the old Stats Section with a new Action Panel */}
            <Paper 
              sx={{ 
                mb: 4,
                p: 3,
                borderRadius: 2,
                background: theme.palette.background.paper
              }}
            >
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Team Organization
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Create and manage groups to organize your team members effectively. 
                      Members can belong to multiple groups based on their roles and responsibilities.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box display="flex" gap={2} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      onClick={handleOpenInviteDialog}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none'
                      }}
                    >
                      Invite Members
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => setAddDialogOpen(true)}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none'
                      }}
                    >
                      New Group
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {/* Groups Management Section */}
            <Paper 
              sx={{ 
                borderRadius: 2,
                overflow: 'hidden'
              }}
            >
              <Box p={3} bgcolor={theme.palette.background.default}>
                <Typography variant="h6" fontWeight="medium">
                  Group Management
                </Typography>
              </Box>
              <Divider />
              <GroupManagement
                groups={groups}
                availableUsers={availableUsers}
                onAddGroup={handleAddGroup}
                onDeleteGroup={handleDeleteGroup}
                onEditGroup={handleEditGroup}
                onAddMember={handleAddMember}
                onRemoveMember={handleRemoveMember}
                onInviteMember={handleInviteMember}
                onRefresh={fetchGroups}
                loading={loading}
                error={error}
                isTeamLeader={user?.role === 'school_admin' || user?.role === 'admin'}
              />
            </Paper>
          </Box>
        </Fade>
      )}

      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)}>
        <AddGroupForm 
          onSubmit={handleAddGroup}
          onCancel={() => setAddDialogOpen(false)}
        />
      </Dialog>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <EditGroupForm
          group={editingGroup}
          onSubmit={async (data) => {
            await handleEditGroup(editingGroup.id, data);
          }}
          onCancel={() => {
            setEditDialogOpen(false);
            setEditingGroup(null);
          }}
        />
      </Dialog>

      <Dialog 
        open={inviteDialogOpen} 
        onClose={() => setInviteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <InviteMembers 
          groupId={selectedGroup?.id}
          groups={groups}
          onClose={() => setInviteDialogOpen(false)}
          onSuccess={() => {
            setInviteDialogOpen(false);
            fetchGroups();
          }}
        />
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {process.env.NODE_ENV === 'development' && (
        <div style={{ 
          background: '#f5f5f5', 
          padding: '10px', 
          marginBottom: '20px',
          fontSize: '12px'
        }}>
          
        </div>
      )}
    </Container>
  );
};

export default GroupPage;
